import React, { useState, useEffect } from 'react';
import {
    User, Clock, CheckCircle, XCircle, Eye, Filter,
    Search, ChevronDown, ChevronUp, Mail, Phone, AlertCircle,
    Calendar, MessageSquare, Info, ArrowRight, X, Check
} from 'lucide-react';
import './ProfileUpdateRequests.css';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';

const ProfileUpdateRequests = () => {
    const { admin } = useAdminAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedRequest, setExpandedRequest] = useState(null);
    const [stats, setStats] = useState({
        pending: 0,
        approved: 0,
        rejected: 0,
        total: 0
    });

    // Modal states
    const [showActionModal, setShowActionModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [modalAction, setModalAction] = useState('');
    const [adminMessage, setAdminMessage] = useState('');
    const [processingRequest, setProcessingRequest] = useState(false);

    // Toast notification state
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    useEffect(() => {
        fetchRequests();
        fetchStats();
    }, [filter]);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const response = await axios.get(
                `https://api.rismicmart.com/api/admin/profile-update-requests?status=${filter}`
            );
            if (response.data.success) {
                setRequests(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching requests:', error);
            showToast('Failed to fetch requests', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get(
                'https://api.rismicmart.com/api/admin/profile-update-requests/stats'
            );
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };

    const handleActionClick = (request, action) => {
        setSelectedRequest(request);
        setModalAction(action);
        setAdminMessage('');
        setShowActionModal(true);
    };

    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setShowDetailsModal(true);
    };

    const handleConfirmAction = async () => {
        if (!adminMessage.trim()) {
            showToast('Please provide a message', 'error');
            return;
        }

        setProcessingRequest(true);
        try {
            const requestBody = {
                action: modalAction,
                message: adminMessage.trim()
            };

            if (admin) {
                if (admin._id || admin.id) {
                    requestBody.adminId = admin._id || admin.id;
                }
                if (admin.name || admin.fullName || admin.email) {
                    requestBody.adminName = admin.name || admin.fullName || admin.email;
                }
            }

            const response = await axios.post(
                `https://api.rismicmart.com/api/admin/profile-update-requests/${selectedRequest._id}/handle`,
                requestBody
            );

            if (response.data.success) {
                showToast(`Request ${modalAction}d successfully!`, 'success');
                setShowActionModal(false);
                setSelectedRequest(null);
                setAdminMessage('');
                fetchRequests();
                fetchStats();
            }
        } catch (error) {
            console.error('Error processing request:', error);
            showToast(error.response?.data?.message || 'Failed to process request', 'error');
        } finally {
            setProcessingRequest(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return '#ff9800';
            case 'approved': return '#10b981';
            case 'rejected': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <Clock size={16} />;
            case 'approved': return <CheckCircle size={16} />;
            case 'rejected': return <XCircle size={16} />;
            default: return null;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredRequests = requests.filter(request => {
        const searchLower = searchTerm.toLowerCase();
        return (
            request.deliveryPersonId?.fullName?.toLowerCase().includes(searchLower) ||
            request.deliveryPersonId?.email?.toLowerCase().includes(searchLower) ||
            request.deliveryPersonId?.phone?.includes(searchTerm) ||
            request.reason?.toLowerCase().includes(searchLower)
        );
    });

    return (
        <div className="profile-update-requests-container">
            {/* Toast Notification */}
            {toast.show && (
                <div className={`toast-notification ${toast.type}`}>
                    {toast.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
                    <span>{toast.message}</span>
                </div>
            )}

            {/* Header Section */}
            <div className="pur-header">
                <div className="pur-header-top">
                    <div className="pur-title-section">
                        <h1 className="pur-main-title">Profile Update Requests</h1>
                        <p className="pur-subtitle">Review and manage delivery personnel profile change requests</p>
                        {admin && (
                            <div className="pur-admin-badge">
                                <User size={14} />
                                <span>{admin.name || admin.fullName || admin.email}</span>
                            </div>
                        )}
                    </div>

                    {/* Stats Cards */}
                    <div className="pur-stats-grid">
                        <div className="pur-stat-card total">
                            <div className="stat-icon">
                                <Filter size={24} />
                            </div>
                            <div className="stat-content">
                                <span className="stat-value">{stats.total}</span>
                                <span className="stat-label">Total Requests</span>
                            </div>
                        </div>
                        <div className="pur-stat-card pending">
                            <div className="stat-icon">
                                <Clock size={24} />
                            </div>
                            <div className="stat-content">
                                <span className="stat-value">{stats.pending}</span>
                                <span className="stat-label">Pending</span>
                            </div>
                        </div>
                        <div className="pur-stat-card approved">
                            <div className="stat-icon">
                                <CheckCircle size={24} />
                            </div>
                            <div className="stat-content">
                                <span className="stat-value">{stats.approved}</span>
                                <span className="stat-label">Approved</span>
                            </div>
                        </div>
                        <div className="pur-stat-card rejected">
                            <div className="stat-icon">
                                <XCircle size={24} />
                            </div>
                            <div className="stat-content">
                                <span className="stat-value">{stats.rejected}</span>
                                <span className="stat-label">Rejected</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filter Section */}
                <div className="pur-controls">
                    <div className="pur-search-wrapper">
                        <Search size={20} className="search-icon" />
                        <input
                            type="text"
                            className="pur-search-input"
                            placeholder="Search by name, email, phone, or reason..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="search-clear" onClick={() => setSearchTerm('')}>
                                <X size={16} />
                            </button>
                        )}
                    </div>

                    <div className="pur-filter-tabs">
                        <button
                            className={`pur-filter-tab ${filter === 'pending' ? 'active' : ''}`}
                            onClick={() => setFilter('pending')}
                        >
                            <Clock size={16} />
                            Pending
                            <span className="tab-badge">{stats.pending}</span>
                        </button>
                        <button
                            className={`pur-filter-tab ${filter === 'approved' ? 'active' : ''}`}
                            onClick={() => setFilter('approved')}
                        >
                            <CheckCircle size={16} />
                            Approved
                            <span className="tab-badge">{stats.approved}</span>
                        </button>
                        <button
                            className={`pur-filter-tab ${filter === 'rejected' ? 'active' : ''}`}
                            onClick={() => setFilter('rejected')}
                        >
                            <XCircle size={16} />
                            Rejected
                            <span className="tab-badge">{stats.rejected}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="pur-content">
                {loading ? (
                    <div className="pur-loading-state">
                        <div className="pur-spinner"></div>
                        <p>Loading requests...</p>
                    </div>
                ) : filteredRequests.length === 0 ? (
                    <div className="pur-empty-state">
                        <div className="empty-icon">
                            <AlertCircle size={64} />
                        </div>
                        <h3>No requests found</h3>
                        <p>
                            {searchTerm
                                ? 'No requests match your search criteria'
                                : `No ${filter} requests at the moment`}
                        </p>
                    </div>
                ) : (
                    <div className="pur-requests-grid">
                        {filteredRequests.map((request) => (
                            <div key={request._id} className="pur-request-card">
                                {/* Card Header */}
                                <div className="pur-card-header">
                                    <div className="person-info">
                                        <div className="person-avatar-wrapper">
                                            <img
                                                src={
                                                    request.deliveryPersonId?.profileImage?.url ||
                                                    `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                                        request.deliveryPersonId?.fullName || 'User'
                                                    )}&background=007b68&color=fff&size=80`
                                                }
                                                alt={request.deliveryPersonId?.fullName}
                                                className="person-avatar"
                                                onError={(e) => {
                                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                                        request.deliveryPersonId?.fullName || 'User'
                                                    )}&background=007b68&color=fff&size=80`;
                                                }}
                                            />
                                            <div className="avatar-status-indicator"></div>
                                        </div>
                                        <div className="person-details">
                                            <h3 className="person-name">{request.deliveryPersonId?.fullName || 'N/A'}</h3>
                                            <div className="person-contact-info">
                                                <span className="contact-item">
                                                    <Mail size={14} />
                                                    {request.deliveryPersonId?.email || 'N/A'}
                                                </span>
                                                <span className="contact-item">
                                                    <Phone size={14} />
                                                    {request.deliveryPersonId?.phone || 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-meta">
                                        <span
                                            className="status-badge"
                                            style={{ backgroundColor: getStatusColor(request.status) }}
                                        >
                                            {getStatusIcon(request.status)}
                                            {request.status}
                                        </span>
                                        <span className="request-date-badge">
                                            <Calendar size={12} />
                                            {formatDate(request.createdAt)}
                                        </span>
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className="pur-card-content">
                                    <div className="reason-section">
                                        <div className="section-header">
                                            <MessageSquare size={16} />
                                            <strong>Reason for Update</strong>
                                        </div>
                                        <p className="reason-text">{request.reason}</p>
                                    </div>

                                    <div className="changes-section">
                                        <div className="section-header">
                                            <Info size={16} />
                                            <strong>Changes Requested</strong>
                                        </div>
                                        <div className="changes-tags">
                                            {request.requestedChanges.fullName && (
                                                <span className="change-tag">
                                                    <User size={12} />
                                                    Name
                                                </span>
                                            )}
                                            {request.requestedChanges.email && (
                                                <span className="change-tag">
                                                    <Mail size={12} />
                                                    Email
                                                </span>
                                            )}
                                            {request.requestedChanges.phone && (
                                                <span className="change-tag">
                                                    <Phone size={12} />
                                                    Phone
                                                </span>
                                            )}
                                            {request.requestedChanges.profileImage && (
                                                <span className="change-tag">
                                                    <User size={12} />
                                                    Profile Image
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Card Actions */}
                                <div className="pur-card-actions">
                                    <button
                                        className="btn-view-details"
                                        onClick={() => handleViewDetails(request)}
                                    >
                                        <Eye size={16} />
                                        View Full Details
                                        <ArrowRight size={14} />
                                    </button>

                                    {request.status === 'pending' && (
                                        <div className="action-buttons-group">
                                            <button
                                                className="btn-approve-modern"
                                                onClick={() => handleActionClick(request, 'approve')}
                                            >
                                                <CheckCircle size={16} />
                                                Approve
                                            </button>
                                            <button
                                                className="btn-reject-modern"
                                                onClick={() => handleActionClick(request, 'reject')}
                                            >
                                                <XCircle size={16} />
                                                Reject
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Details Modal */}
            {showDetailsModal && selectedRequest && (
                <div className="pur-modal-overlay" onClick={() => setShowDetailsModal(false)}>
                    <div className="pur-modal-content details-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pur-modal-header">
                            <div>
                                <h2>Request Details</h2>
                                <p className="modal-subtitle">Complete information about this profile update request</p>
                            </div>
                            <button className="modal-close-btn" onClick={() => setShowDetailsModal(false)}>
                                <X size={24} />
                            </button>
                        </div>

                        <div className="pur-modal-body">
                            {/* Delivery Person Info */}
                            <div className="details-section">
                                <h3 className="section-title">Delivery Person Information</h3>
                                <div className="info-grid">
                                    <div className="info-item">
                                        <span className="info-label">Full Name</span>
                                        <span className="info-value">{selectedRequest.deliveryPersonId?.fullName}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Email</span>
                                        <span className="info-value">{selectedRequest.deliveryPersonId?.email}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Phone</span>
                                        <span className="info-value">{selectedRequest.deliveryPersonId?.phone}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Request Date</span>
                                        <span className="info-value">{formatDate(selectedRequest.createdAt)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Reason */}
                            <div className="details-section">
                                <h3 className="section-title">Reason for Update</h3>
                                <p className="reason-detail">{selectedRequest.reason}</p>
                            </div>

                            {/* Requested Changes */}
                            <div className="details-section">
                                <h3 className="section-title">Requested Changes</h3>
                                <div className="changes-comparison-grid">
                                    {selectedRequest.requestedChanges.fullName && (
                                        <div className="comparison-item">
                                            <label>Full Name</label>
                                            <div className="comparison-values">
                                                <div className="old-value">
                                                    <span className="value-label">Current</span>
                                                    <span>{selectedRequest.currentData.fullName}</span>
                                                </div>
                                                <ArrowRight className="arrow-icon" size={20} />
                                                <div className="new-value">
                                                    <span className="value-label">New</span>
                                                    <span>{selectedRequest.requestedChanges.fullName}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedRequest.requestedChanges.email && (
                                        <div className="comparison-item">
                                            <label>Email Address</label>
                                            <div className="comparison-values">
                                                <div className="old-value">
                                                    <span className="value-label">Current</span>
                                                    <span>{selectedRequest.currentData.email}</span>
                                                </div>
                                                <ArrowRight className="arrow-icon" size={20} />
                                                <div className="new-value">
                                                    <span className="value-label">New</span>
                                                    <span>{selectedRequest.requestedChanges.email}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedRequest.requestedChanges.phone && (
                                        <div className="comparison-item">
                                            <label>Phone Number</label>
                                            <div className="comparison-values">
                                                <div className="old-value">
                                                    <span className="value-label">Current</span>
                                                    <span>{selectedRequest.currentData.phone}</span>
                                                </div>
                                                <ArrowRight className="arrow-icon" size={20} />
                                                <div className="new-value">
                                                    <span className="value-label">New</span>
                                                    <span>{selectedRequest.requestedChanges.phone}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedRequest.requestedChanges.profileImage && (
                                        <div className="comparison-item image-comparison">
                                            <label>Profile Image</label>
                                            <div className="image-comparison-wrapper">
                                                <div className="image-box">
                                                    <span className="image-label">Current Image</span>
                                                    <img
                                                        src={selectedRequest.currentData.profileImage?.url || ''}
                                                        alt="Current"
                                                        onError={(e) => {
                                                            e.target.src = `https://ui-avatars.com/api/?name=Current&background=ccc&color=666`;
                                                        }}
                                                    />
                                                </div>
                                                <ArrowRight className="arrow-icon" size={24} />
                                                <div className="image-box">
                                                    <span className="image-label">New Image</span>
                                                    <img
                                                        src={selectedRequest.requestedChanges.profileImage?.url}
                                                        alt="New"
                                                        onError={(e) => {
                                                            e.target.src = `https://ui-avatars.com/api/?name=New&background=007b68&color=fff`;
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Admin Response */}
                            {selectedRequest.adminResponse && (
                                <div className="details-section admin-response-section">
                                    <h3 className="section-title">Admin Response</h3>
                                    <div className="admin-response-card">
                                        <p className="response-message">{selectedRequest.adminResponse.message}</p>
                                        <div className="response-meta">
                                            {selectedRequest.adminResponse.respondedByName && (
                                                <span className="responder">
                                                    <User size={14} />
                                                    {selectedRequest.adminResponse.respondedByName}
                                                </span>
                                            )}
                                            <span className="response-date">
                                                <Calendar size={14} />
                                                {formatDate(selectedRequest.adminResponse.respondedAt)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="pur-modal-footer">
                            {selectedRequest.status === 'pending' && (
                                <>
                                    <button
                                        className="btn-modal-approve"
                                        onClick={() => {
                                            setShowDetailsModal(false);
                                            handleActionClick(selectedRequest, 'approve');
                                        }}
                                    >
                                        <CheckCircle size={18} />
                                        Approve Request
                                    </button>
                                    <button
                                        className="btn-modal-reject"
                                        onClick={() => {
                                            setShowDetailsModal(false);
                                            handleActionClick(selectedRequest, 'reject');
                                        }}
                                    >
                                        <XCircle size={18} />
                                        Reject Request
                                    </button>
                                </>
                            )}
                            <button
                                className="btn-modal-close"
                                onClick={() => setShowDetailsModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Action Confirmation Modal */}
            {showActionModal && selectedRequest && (
                <div className="pur-modal-overlay" onClick={() => setShowActionModal(false)}>
                    <div className="pur-modal-content action-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pur-modal-header">
                            <div>
                                <h2 className={`action-title ${modalAction}`}>
                                    {modalAction === 'approve' ? (
                                        <CheckCircle size={28} />
                                    ) : (
                                        <XCircle size={28} />
                                    )}
                                    {modalAction === 'approve' ? 'Approve' : 'Reject'} Request
                                </h2>
                                <p className="modal-subtitle">
                                    {modalAction === 'approve'
                                        ? 'This will apply the requested changes to the delivery person profile'
                                        : 'This will decline the profile update request'}
                                </p>
                            </div>
                            <button className="modal-close-btn" onClick={() => setShowActionModal(false)}>
                                <X size={24} />
                            </button>
                        </div>

                        <div className="pur-modal-body">
                            <div className="request-summary-card">
                                <div className="summary-row">
                                    <span className="summary-label">Delivery Person</span>
                                    <span className="summary-value">{selectedRequest?.deliveryPersonId?.fullName}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="summary-label">Request Date</span>
                                    <span className="summary-value">{formatDate(selectedRequest?.createdAt)}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="summary-label">Reason</span>
                                    <span className="summary-value reason">{selectedRequest?.reason}</span>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Admin Message <span className="required">*</span>
                                    <span className="char-counter">{adminMessage.length}/500</span>
                                </label>
                                <textarea
                                    className="form-textarea"
                                    value={adminMessage}
                                    onChange={(e) => setAdminMessage(e.target.value.slice(0, 500))}
                                    placeholder={`Provide a detailed reason for ${modalAction === 'approve' ? 'approving' : 'rejecting'} this request...`}
                                    rows="6"
                                />
                                {adminMessage.trim().length === 0 && (
                                    <p className="form-error">
                                        <AlertCircle size={14} />
                                        Message is required to proceed
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="pur-modal-footer">
                            <button
                                className="btn-modal-cancel"
                                onClick={() => setShowActionModal(false)}
                                disabled={processingRequest}
                            >
                                Cancel
                            </button>
                            <button
                                className={`btn-modal-confirm ${modalAction}`}
                                onClick={handleConfirmAction}
                                disabled={processingRequest || !adminMessage.trim()}
                            >
                                {processingRequest ? (
                                    <>
                                        <div className="btn-spinner"></div>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        {modalAction === 'approve' ? (
                                            <CheckCircle size={18} />
                                        ) : (
                                            <XCircle size={18} />
                                        )}
                                        Confirm {modalAction === 'approve' ? 'Approval' : 'Rejection'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProfileUpdateRequests;
